import { fromPromise } from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { KEYS, SCREENS } from "utils/constants";
import { API_ORIGIN } from "utils/helpers/apiOrigin";
import { getAuthDescriptor } from "utils/helpers/authProvider";

// Cookie-based refresh (e.g. Zitadel) keeps the "session" sentinel in
// localStorage. Guest / domain-signin flows carry a real JWT in localStorage and
// must not be bounced to the provider's sign-in screen.
const SESSION_SENTINEL = "session";

const isGuestSession = () => {
  const token = localStorage.getItem(KEYS.ACCESS_TOKEN);
  return Boolean(token) && token !== SESSION_SENTINEL;
};

// The active provider's refresh endpoint, if it supports silent refresh.
const getRefreshPath = () => {
  const { paths } = getAuthDescriptor();
  return paths && paths.refresh ? paths.refresh : null;
};

let refreshing = null;

const refreshSession = (refreshPath) => {
  if (!refreshing) {
    refreshing = fetch(`${API_ORIGIN}${refreshPath}`, {
      method: "POST",
      credentials: "include",
    })
      .then((response) => response.ok)
      .catch(() => false)
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
};

const redirectToSignIn = () => {
  try {
    localStorage.clear();
  } catch (error) {
    void error;
  }
  window.location.replace(`/#${SCREENS.AUTH.SIGN_IN.path}`);
};

const refreshLink = onError(({ networkError, operation, forward }) => {
  const statusCode = networkError && networkError.statusCode;
  if (statusCode !== 401 && statusCode !== 403) return;
  if (isGuestSession()) return;
  if (operation.getContext().__refreshed) return;

  const refreshPath = getRefreshPath();
  // Providers without a refresh endpoint (e.g. KAYROS_ID) can't renew the
  // session silently; send the user back to sign in.
  if (!refreshPath) {
    redirectToSignIn();
    return;
  }

  return fromPromise(refreshSession(refreshPath)).flatMap((ok) => {
    operation.setContext({ __refreshed: true });
    if (!ok) {
      redirectToSignIn();
    }
    return forward(operation);
  });
});

export default refreshLink;
